"use client";

import { useState, useEffect, useMemo, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faExclamationTriangle,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";

/**
 * SpaceCountdown component shows a countdown timer for space expiration
 * @param {Object} props - Component props
 * @param {string} props.expirationAt - ISO date string for when the space expires
 * @param {boolean} props.hasConflict - Whether someone else has claimed the slug
 * @param {string} props.spaceSlug - The space slug for messaging
 * @param {Function} props.onPublishClick - Function to call when countdown is clicked
 * @param {Function} props.onChangeSlug - Function to call when URL conflict needs to be resolved
 */
export default function SpaceCountdown({
  expirationAt,
  hasConflict = false,
  spaceSlug,
  onPublishClick,
  onChangeSlug,
}) {
  const [timeLeft, setTimeLeft] = useState(null);
  const [isExpired, setIsExpired] = useState(false);

  // Debug mode for localhost - allows toggling between states
  const [debugState, setDebugState] = useState(0); // 0: normal, 1: expired-available, 2: conflict
  const isLocalhost =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1" ||
      window.location.hostname.includes("localhost"));

  // Debug key handler for localhost
  useEffect(() => {
    if (!isLocalhost) return;

    const handleKeyPress = (e) => {
      // Press 'D' to cycle through debug states
      if (e.key.toLowerCase() === "d" && e.shiftKey) {
        setDebugState((prev) => (prev + 1) % 3);
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [isLocalhost, debugState]);

  // Calculate time left and update every second
  useEffect(() => {
    if (!expirationAt) return;

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const expiration = new Date(expirationAt).getTime();
      const difference = expiration - now;

      if (difference <= 0) {
        setIsExpired(true);
        setTimeLeft(null);
        return;
      }

      const hours = Math.floor(difference / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setTimeLeft({ hours, minutes, seconds });
      setIsExpired(false);
    };

    // Calculate immediately
    calculateTimeLeft();

    // Update every second
    const interval = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(interval);
  }, [expirationAt]);

  // Format time left for display
  const formatTimeLeft = useCallback((time) => {
    if (!time) return "";

    if (time.hours > 0) {
      return `${time.hours}h ${time.minutes}m`;
    } else if (time.minutes > 0) {
      return `${time.minutes}m ${time.seconds}s`;
    } else {
      return `${time.seconds}s`;
    }
  }, []);

  // Determine the countdown state and styling
  const countdownState = useMemo(() => {
    // Debug mode overrides for localhost
    if (isLocalhost && debugState === 1) {
      return {
        type: "expired_available",
        icon: faCheckCircle,
        color: "text-white",
        bgColor: "bg-black",
        borderColor: "border-accent-lime",
        message: "Claim your URL",
        subMessage: "Still available!",
        buttonText: "Publish Now",
        clickable: true,
      };
    } else if (isLocalhost && debugState === 2) {
      return {
        type: "conflict",
        icon: faExclamationTriangle,
        color: "text-white",
        bgColor: "bg-black",
        borderColor: "border-accent-coral",
        message: "URL Taken",
        subMessage: "Someone else claimed it",
        buttonText: "Choose New URL",
        clickable: true,
      };
    }

    // Normal logic
    // Only show conflict if OUR draft is expired AND someone else has an active draft
    if (isExpired && hasConflict) {
      // Show conflict only when our timer has expired and someone else has claimed it
      return {
        type: "conflict",
        icon: faExclamationTriangle,
        color: "text-white",
        bgColor: "bg-black",
        borderColor: "border-red-600",
        message: "URL Taken",
        subMessage: "Someone else claimed it",
        buttonText: "Choose New URL",
        clickable: true,
      };
    } else if (isExpired && !hasConflict) {
      return {
        type: "expired_available",
        icon: faCheckCircle,
        color: "text-white",
        bgColor: "bg-black",
        borderColor: "border-accent-lime",
        message: "Claim your URL",
        subMessage: "Still available!",
        buttonText: "Publish Now",
        clickable: true,
      };
    } else if (timeLeft) {
      const totalMinutes = timeLeft.hours * 60 + timeLeft.minutes;
      const totalSeconds = totalMinutes * 60 + timeLeft.seconds;
      const isUrgent = totalSeconds <= 300; // Last 5 minutes

      return {
        type: "countdown",
        icon: faClock,
        color: "text-white",
        bgColor: isUrgent ? "bg-black" : "bg-black",
        borderColor: isUrgent ? "border-accent-coral" : "border-accent-lime",
        message: formatTimeLeft(timeLeft),
        subMessage: "Claim your URL",
        buttonText: "Publish Now",
        clickable: true,
        isUrgent,
      };
    }

    return null;
  }, [
    timeLeft,
    isExpired,
    hasConflict,
    spaceSlug,
    formatTimeLeft,
    isLocalhost,
    debugState,
  ]);

  if (!countdownState) return null;

  const handleClick = () => {
    if (countdownState.type === "conflict" && onChangeSlug) {
      onChangeSlug();
    } else if (countdownState.clickable && onPublishClick) {
      onPublishClick();
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={!countdownState.clickable}
      className={`
        px-4 py-3 rounded-3xl font-semibold flex items-center gap-3
        transition-all duration-300 transform border
        ${countdownState.bgColor} ${countdownState.borderColor} ${countdownState.color}
        ${
          countdownState.clickable
            ? "hover:scale-105 hover:shadow-lg cursor-pointer active:scale-95"
            : "cursor-not-allowed opacity-75"
        }
        ${countdownState.isUrgent ? "animate-pulse" : ""}
        shadow-lg backdrop-blur-sm
      `}
      title={
        countdownState.clickable
          ? `Click to ${countdownState.buttonText.toLowerCase()}`
          : countdownState.subMessage
      }
    >
      <FontAwesomeIcon
        icon={countdownState.icon}
        className={`${countdownState.color} text-lg`}
      />

      <div className="flex flex-col items-start">
        <span
          className={`text-lg font-bold leading-tight ${
            countdownState.type === "countdown" ? "text-accent-lime" : ""
          }`}
        >
          {countdownState.message}
        </span>
        <span className="text-sm opacity-90 leading-tight">
          {countdownState.subMessage}
        </span>
      </div>

      {countdownState.clickable && (
        <div className="ml-2 opacity-75">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 0L6.59 1.41L12.17 7H0V9H12.17L6.59 14.59L8 16L16 8L8 0Z" />
          </svg>
        </div>
      )}
    </button>
  );
}

/**
 * Hook to determine if we should show countdown instead of unpublished changes
 * @param {Object} spaceData - Space data object
 * @returns {Object} - { shouldShowCountdown, hasConflict, expirationAt }
 */
export function useSpaceCountdownState(spaceData) {
  const [conflictData, setConflictData] = useState({
    hasConflict: false,
    conflictType: null,
  });

  // Check for conflicts when we have a first-time draft
  useEffect(() => {
    if (!spaceData?.space_data) return;

    const space = spaceData.space_data;
    const isFirstTimeDraft =
      space.status === "draft" && space.version_number === 0;

    if (!isFirstTimeDraft || !space.expiration_at) return;

    const checkConflicts = async () => {
      try {
        // Use the updated check-slug edge function with user_id parameter
        const response = await fetch(
          `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/check-slug?slug=${space.slug}&user_id=${space.user_id}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY}`,
              "Content-Type": "application/json",
              Accept: "application/json",
            },
          }
        );

        if (!response.ok) {
          console.error(
            "Error checking slug:",
            response.status,
            response.statusText
          );
          return;
        }

        const data = await response.json();

        // Determine conflict status based on the response
        let hasConflict = false;
        let conflictType = null;

        if (!data.available) {
          // URL is not available to this user
          if (data.reason === "published" && data.owner_id !== space.user_id) {
            // Someone else has published this URL
            hasConflict = true;
            conflictType = "published";
          } else if (
            data.reason === "draft_active" &&
            data.owner_id !== space.user_id
          ) {
            // Someone else has an active draft for this URL
            hasConflict = true;
            conflictType = "draft_active";
          }
        } else if (data.available && data.reason) {
          // URL is available to this user, but check the reason
          if (
            data.reason === "owned_draft" ||
            data.reason === "owned_published" ||
            data.reason === "owned_expired"
          ) {
            // User owns the existing space, no conflict
            hasConflict = false;
          }
        }

        setConflictData({
          hasConflict,
          conflictType,
        });
      } catch (err) {
        console.error("Failed to check conflicts:", err);
      }
    };

    checkConflicts();
  }, [
    spaceData?.space_data?.slug,
    spaceData?.space_data?.user_id,
    spaceData?.space_data?.status,
  ]);

  return useMemo(() => {
    if (!spaceData?.space_data) {
      return {
        shouldShowCountdown: false,
        hasConflict: false,
        expirationAt: null,
      };
    }

    const space = spaceData.space_data;

    // Only show countdown for draft spaces that have never been published
    const isFirstTimeDraft =
      space.status === "draft" && space.version_number === 0;
    const hasExpiration = Boolean(space.expiration_at);

    // Hide countdown if space is published or has been published before
    const isPublished = space.status === "published";
    const hasBeenPublished = space.version_number > 0;

    return {
      shouldShowCountdown:
        isFirstTimeDraft && hasExpiration && !isPublished && !hasBeenPublished,
      hasConflict: conflictData.hasConflict,
      expirationAt: space.expiration_at,
    };
  }, [spaceData, conflictData]);
}
